import { AppSettings } from "@/AppSettings";
import { IconDefinition, IconName, IconPrefix } from "@fortawesome/fontawesome-svg-core"
import { FontAwesomeIcon, FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import { ComponentType, FC, lazy, useMemo } from "react";

export interface IconProps extends Omit<FontAwesomeIconProps, 'icon'> {
    icon: { prefix: IconPrefix, iconName: IconName };
}

export interface IconModule {
    default: {
        definition: IconDefinition;
        width: number;
        height: number;
        prefix: IconPrefix;
        iconName: IconName;
        aliases: string[];
        ligatures: string[];
    }
}

enum IconModuleStatus {
    Pending = 1,
    Success = 2,
    Error = 3
}

interface IconLazyModule {
    importer: () => Promise<IconModule>;
    status: IconModuleStatus,
    result?: IconDefinition,
    error?: any,
    useIcon: () => (IconDefinition | undefined);
    promise?: Promise<void>;
}

interface IconModuleGlobCreator {
    (parentPath: string): Record<IconName, () => Promise<IconModule>>
}

export const createLazyLoadedIconComponent = (prefix: IconPrefix) => {
    return lazy(async () => {
        const createModules = await loadGlobImports(AppSettings.fontAwesomeParentPath, prefix);
        const modules = createModules(AppSettings.fontAwesomeParentPath);
        const icons = processIconModules(modules);
        
        const Icon: FC<IconProps> = ({ icon, ...rest }) => {
            const useIcon = icons[icon.iconName]?.useIcon;
            
            if (!useIcon) {
                throw Error('Icon Not Found')
            }

            const iconDefinition = useIcon();
        
            if (!iconDefinition) {
                return null;
            }
        
            return (
                <FontAwesomeIcon icon={iconDefinition} {...rest} />
            )
        }
    
        return { default: Icon };
    }) as ComponentType<IconProps>;
}

const globImports: Record<IconPrefix, Promise<IconModuleGlobCreator> | undefined> = {
    fab: undefined, // brand
    fad: undefined, // duotone solid
    fadl: undefined, // duotone light
    fadr: undefined, // duotone regular
    fadt: undefined, // duotone thin
    fak: undefined, // UNKNOWN?!?!?
    fakd: undefined, // UNKNOWN?!?!?!
    fal: undefined, // light
    far: undefined, // regular
    fas: undefined, // solid
    fasdl: undefined, // sharp duotone light
    fasdr: undefined, // sharp duotone regular
    fasds: undefined, // sharp duotone solid,
    fasdt: undefined, // sharp duotone thin
    fasl: undefined, //sharp light
    fasr: undefined, //sharp regular
    fass: undefined, // sharp solid
    fast: undefined, // sharp thin
    fat: undefined, // thin
}

export const loadGlobImports = async (parentPath: string, prefix: IconPrefix) => {
    if (!globImports[prefix]) {
        globImports[prefix] = new Promise<IconModuleGlobCreator>((resolve, _reject) => {
            const scriptTag = document.createElement('script');
            scriptTag.id = `fa-loader-${prefix}`;

            scriptTag.onload = () => {
                resolve((window as any)[`${prefix}CreateMap`]);
            }

            scriptTag.src = `${parentPath}/${prefix}.js`

            document.head.appendChild(scriptTag);
        })
    }

    return await globImports[prefix];

}

export const processIconModules = (globModules: Record<string, () => Promise<IconModule>>) => {
    return Object.keys(globModules).reduce<Record<string, IconLazyModule>>((modules, current) => {
        const module: IconLazyModule = {
            importer: globModules[current],
            status: IconModuleStatus.Pending,
            error: undefined,
            useIcon: undefined as any
        }

        module.useIcon = () => {
            if (module.status === IconModuleStatus.Error) {
                throw module.error;
            }
            else if (module.status === IconModuleStatus.Success) {
                return module.result;
            }
            else if (module.promise) {
                throw module.promise;
            }
            else {
                module.promise = module.importer()
                    .then(r => {
                        module.result = r.default.definition;
                        module.status = IconModuleStatus.Success;
                    })
                    .catch(e => {
                        module.error = e;
                        module.status = IconModuleStatus.Error;
                    });

                throw module.promise;
            }
        }
        const fileName = current;

        modules[fileName] = module;

        return modules;
    }, {});
}


const capitalize = (str: string) => {
    return str[0].toUpperCase() + str.slice(1)
}

const camelCase = (str: string) => {
    return str
        .split('-')
        .map((s, index) => {
            return (index === 0 ? s[0].toLowerCase() : s[0].toUpperCase()) + s.slice(1).toLowerCase()
        })
        .join('')
}

export const useIconFileName = (iconName: IconName) => {
    const fileName = useMemo(() => {
        return `fa${capitalize(camelCase(iconName))}`;
    }, [iconName]);

    return fileName;
}